import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';
import {
  APP_CONTAINER_WIDTH,
  SECTION_SPACING
} from '@styles/constants/spacings';
import React from 'react';
import {
  DataType,
  DATA_TYPES,
  VIEW_TYPES
} from 'types/unacademyIcons/anyfeed.type';
import { CARD_TYPES } from '@constants/unacademyIcons/anyfeed.constants';
import AnyfeedSectionHeader from '@unicons-comp/AnyfeedSectionHeader/AnyfeedSectionHeader';
import AnyfeedCourse from '@unicons-comp/AnyfeedCourse/AnyfeedCourse';
import AnyfeedLesson from '@unicons-comp/AnyfeedLesson/AnyfeedLesson';
import useInfiniteScrollHandler from '@hooks/useInfiniteScrollHandler';
import useInfiniteFetch from '@hooks/useInfiniteFetch';
import {
  ICONS_ANYFEED,
  ICONS_ANYFEED_DETAIL
} from '@constants/unacademyIcons/iconsEndpoints.constants';
import {
  getFetcherOptions,
  getRequestUrl
} from '@utils/api-helper/getUAIconsOptions';
import AnyfeedITCL from '@unicons-comp/AnyfeedITCL/AnyfeedITCL';
import isEmpty from '@utils/isEmpty';
import AnyfeedList from '@unicons-comp/AnyfeedList/AnyfeedList';
import AnyfeedCATCL from '@unicons-comp/AnyfeedCATCL/AnyfeedCATCL';
import AnyfeedViewAll from '@unicons-comp/AnyfeedViewAll/AnyfeedViewAll';
import AnyfeedKeyValue from '@unicons-comp/AnyfeedKeyValue/AnyfeedKeyValue';
import { useSelector } from 'react-redux';
import { ICONS_GOALS_KEY } from '@constants/unacademyIcons/icons.constants';
import { getIsSubscribedToPlus } from '@api/selectors/subscription';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { emptyObject } from '@constants/empty';
import useConstants from '@api/hooks/useConstants';

const SUPPORTED_SECTIONS = [
  DATA_TYPES.COURSE,
  DATA_TYPES.LESSON,
  DATA_TYPES.ITCL,
  DATA_TYPES.CATCL,
  DATA_TYPES.VTCL,
  DATA_TYPES.LIST,
  DATA_TYPES.CATCL,
  DATA_TYPES.KEY_VALUE
];
const SUPPORTED_COURSE_CARDS = [
  CARD_TYPES.VERTICAL_LIST_GRID,
  CARD_TYPES.GRID,
  CARD_TYPES.VERTICAL_LIST,
  CARD_TYPES.HORIZONTAL_LIST
];
const SUPPORTED_LESSON_CARDS = [CARD_TYPES.HORIZONTAL_LIST];

const SUPPORTED_LIST_CARDS = [CARD_TYPES.GRID];

const SUPPORTED_KEY_VALUE_CARDS = [CARD_TYPES.HERO];
interface AnyfeedProps {
  anyfeed: any;
  count: number;
  limit: number;
  showViewMore: boolean;
  feedId?: string | null;
}

const getSupportedSection = (section) => {
  const { cardType, dataType } = section;
  if (!SUPPORTED_SECTIONS.includes(dataType)) {
    return false;
  }
  if (dataType === DATA_TYPES.COURSE) {
    return SUPPORTED_COURSE_CARDS.includes(cardType);
  }
  if (dataType === DATA_TYPES.LESSON) {
    return SUPPORTED_LESSON_CARDS.includes(cardType);
  }
  if (dataType === DATA_TYPES.LIST) {
    return SUPPORTED_LIST_CARDS.includes(cardType);
  }
  if (dataType === DATA_TYPES.ITCL) return true;
  if (dataType === DATA_TYPES.VTCL) return true;
  if (dataType === DATA_TYPES.CATCL) return true;
  if (dataType === DATA_TYPES.KEY_VALUE)
    return SUPPORTED_KEY_VALUE_CARDS.includes(cardType);
  return false;
};

const StyledAnyfeedWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0px auto;
  flex-direction: column;
  margin-bottom: 80px;
  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin-bottom: 40px;
  }
`;

type SectionWrapperType = {
  $isHeader: boolean;
  $isSubscribed: boolean;
};

const StyledSectionWrapper = styled.div<SectionWrapperType>`
  max-width: 100%;
  background: ${({ $isHeader }) =>
    $isHeader && 'var(--color-i-icon-dark-primary)'};
  width: ${({ $isHeader }) => ($isHeader ? '100%' : APP_CONTAINER_WIDTH)};
  margin: ${({ $isHeader }) =>
    $isHeader ? '0px auto' : `${SECTION_SPACING.DESKTOP} auto`};
  margin: ${({ $isSubscribed }) =>
    $isSubscribed && `${SECTION_SPACING.MOBILE} auto`};
  ${mediaQuery.uptoIconTablet} {
    width: 100%;
    background: transparent;
    margin: ${({ $isHeader }) =>
      $isHeader ? '0px' : `0px 0px ${SECTION_SPACING.MOBILE}`};
  }
`;

const Anyfeed: React.FC<AnyfeedProps> = ({
  anyfeed,
  count,
  limit,
  showViewMore,
  feedId
}) => {
  const {
    data: anyfeedData,
    size,
    setSize,
    isLoading
  } = useInfiniteFetch(
    getRequestUrl(feedId ? `${ICONS_ANYFEED_DETAIL}/${feedId}` : ICONS_ANYFEED),
    getFetcherOptions({ limit }),
    {
      fallbackData: anyfeed,
      persistSize: true,
      revalidateFirstPage: false,
      revalidateAll: false
    }
  );

  const { constants } = useConstants();
  const iconsConstantGoalUid = constants?.[ICONS_GOALS_KEY]?.[0] || null;
  const { selectedGoal } = useSelectedGoal();

  const iconGoalUID = selectedGoal?.uid || iconsConstantGoalUid;

  const isIconsSubscribed = useSelector(getIsSubscribedToPlus(iconGoalUID));

  const onScrollEnd = () => {
    if (size * limit <= count && !isLoading && isFetching) {
      setSize(size + 1);
      (setIsFetching as any)(false);
    }
  };

  const [isFetching, setIsFetching] = useInfiniteScrollHandler(
    // eslint-disable-next-line no-use-before-define
    onScrollEnd,
    null
  );

  const isDetailedView = anyfeed?.extendedViewType === VIEW_TYPES.DETAILED;

  const getSectionContent = (section, index) => {
    const { data, ...sectionData } = section;
    const isHeader =
      (index === 0 &&
        sectionData.dataType === DATA_TYPES.ITCL &&
        !isIconsSubscribed) ||
      sectionData.dataType === DATA_TYPES.VTCL;
    switch (section.dataType) {
      case DATA_TYPES.COURSE:
        return <AnyfeedCourse data={sectionData} list={data} />;
      case DATA_TYPES.LESSON:
        return <AnyfeedLesson data={sectionData} list={data} />;
      case DATA_TYPES.ITCL:
      case DATA_TYPES.VTCL:
        return (
          <AnyfeedITCL data={sectionData} list={data} isHeader={isHeader} />
        );
      case DATA_TYPES.CATCL:
        return <AnyfeedCATCL data={sectionData} list={data} />;
      case DATA_TYPES.LIST:
        return <AnyfeedList data={sectionData} list={data} />;
      case DATA_TYPES.KEY_VALUE:
        return <AnyfeedKeyValue data={sectionData} list={data} />;
      default:
        return null;
    }
  };
  return (
    <StyledAnyfeedWrapper>
      {anyfeedData &&
        (isDetailedView ? [anyfeedData] || [] : anyfeedData?.results || [])
          .filter((section) => getSupportedSection(section))
          .map((section: DataType, index) => (
            <StyledSectionWrapper
              key={section.uuid}
              $isHeader={
                (index === 0 &&
                  section.dataType === DATA_TYPES.ITCL &&
                  !isIconsSubscribed) ||
                section.dataType === DATA_TYPES.VTCL
              }
              $isSubscribed={isIconsSubscribed}
            >
              {/* Title & icons component */}
              {!isEmpty(section.title) && (
                <AnyfeedSectionHeader
                  title={section.title}
                  titleType={section.titleType}
                  iconSrc={section?.icon}
                  hasActiveSubscription={isIconsSubscribed}
                />
              )}
              {/* Data type component */}
              {getSectionContent(section, index)}
              {/* View more component */}
              {section.isViewMore && showViewMore && (
                <AnyfeedViewAll sectionId={section.uuid} />
              )}
            </StyledSectionWrapper>
          ))}
    </StyledAnyfeedWrapper>
  );
};

Anyfeed.displayName = 'Anyfeed';

export default Anyfeed;
